<script setup>
  import {inject, ref, watch} from "vue";

  import DeveloperContainer from "@components/_DevOnly/DeveloperContainer.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentPatientReleaseConfidentiality from "@components/Dialogs/PatientReleaseConfidentiality/PatientReleaseConfidentiality.vue";
  import {router} from "@inertiajs/vue3";
  import {MedButton, MedCheckbox, MedFormField, MedLabel} from "@pharma4u/medicheck-components";
  import readDom from "@utils/Helpers/ReadDom.js";

  defineOptions({
    name: "ComponentRecordPrintPdfDialog",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    record: {
      type: Object,
      required: true,
    },
  });

  const broadcast = inject("$broadcast");
  const pendingAjaxRequests = inject("$pendingAjaxRequests");
  const simulateResultConfig = inject("simulateResultConfig");
  const restoreResultConfig = inject("restoreResultConfig");
  const temporaryConfig = inject("temporaryConfig");
  const setTemporaryConfig = inject("setTemporaryConfig");
  const axios = inject("$axios"); // kept for preview() blob request

  const refDialog = ref("null");
  const type = ref(null);

  const isLoading = ref(false);

  const printWithDoctorFeedback = ref(temporaryConfig.printWithDoctorFeedback);

  watch(type, (newValue) => {
    switch (newValue) {
      case "doc":
        simulateResultConfig("showCommunicationDoc", true);
        simulateResultConfig("showCommunicationPatient", false);
        break;
      case "patient":
        simulateResultConfig("showCommunicationDoc", false);
        simulateResultConfig("showCommunicationPatient", true);
        break;
      case "archive":
        simulateResultConfig("showCommunicationDoc", false);
        simulateResultConfig("showCommunicationPatient", false);
        simulateResultConfig("veryhighrisk", true);
        simulateResultConfig("highrisk", true);
        simulateResultConfig("midrisk", true);
        simulateResultConfig("lowrisk", true);
        break;
    }
  });

  watch(printWithDoctorFeedback, (newValue) => {
    setTemporaryConfig("printWithDoctorFeedback", newValue);
  });

  function open() {
    type.value = null;
    refDialog.value.open();
  }

  function close() {
    restoreResultConfig();
    refDialog.value.close();
  }

  function debugHtml() {
    isLoading.value = true;
    axios
      .post(
        route("records.print", {patient: props.patient.id, record: props.record.id}),
        {
          type: type.value,
          html: readDom(type.value),
          debug: true,
        },
        {responseType: "blob"},
      )
      .then((response) => {
        const url = globalThis.URL.createObjectURL(new Blob([response.data], {type: "text/html"}));
        globalThis.open(url, "_blank");
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        isLoading.value = false;
      });
  }

  function print() {
    isLoading.value = true;

    // readDom() contains the rendered html without data attributes
    // which is the html string we want to print
    axios
      .post(
        route("records.print", {patient: props.patient.id, record: props.record.id}),
        {
          type: type.value,
          html: readDom(type.value),
        },
        {
          responseType: "blob", // important
        },
      )
      .then((response) => {
        const contentDisposition = response.headers["content-disposition"];
        let filename = "file.pdf"; // default filename (try to get it from header...)
        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          let matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replaceAll(/['"]/g, "");
          }
        }

        const url = globalThis.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        link.style.display = "none";
        document.body.append(link);
        link.click();
        link.remove();

        // force reload to trigger update of easyCom icon
        router.reload({only: ["record"]});
        // reload list of records
        broadcast.postMessage({action: "reload.records"});
        // reload specific record
        broadcast.record.postMessage(props.record.id, {action: "reload.record"});

        close();
        isLoading.value = false;
      })
      .catch((error) => {
        console.error(error);
        isLoading.value = false;
      });
  }

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refDialog"
    dialog-title="PDF erstellen / drucken"
    dialog-width="small"
    :is-loading="isLoading"
    :test-id="getTestId('print-pdf')"
    @dialog-closed="restoreResultConfig()"
  >
    <template #content>
      <ComponentPatientReleaseConfidentiality :patient="patient" />

      <div class="mt-4 border-t-1 pt-4">
        <ul class="space-y-3">
          <li>
            <label for="type-doc" class="flex items-center space-x-2">
              <input id="type-doc" v-model="type" type="radio" class="text-mcred" value="doc" />
              <div>PDF an Arzt</div>
            </label>

            <div v-if="type === 'doc'" class="mt-2 ml-6">
              <MedFormField orientation="horizontal">
                <MedCheckbox
                  v-model="printWithDoctorFeedback"
                  variant="primary"
                  :data-test="getTestId('print-pdf-dialog-doctor-feedback-checkbox')"
                />

                <MedLabel>Arzt kann digital Rückmeldung geben</MedLabel>
              </MedFormField>
            </div>
          </li>

          <li>
            <label for="type-patient" class="flex items-center space-x-2">
              <input id="type-patient" v-model="type" type="radio" class="text-mcred" value="patient" />
              <div>PDF an Patienten</div>
            </label>
          </li>

          <li>
            <label for="type-archiv-pdf" class="flex items-center space-x-2">
              <input id="type-archiv-pdf" v-model="type" type="radio" class="text-mcred" value="archive" />

              <div class="leading-tight">
                Archiv-PDF (alle Ergebnisse)
                <br />
                <span class="text-xs">ggf. für Ihre eigene Ablage, bitte nicht an Arzt / Patienten</span>
              </div>
            </label>
          </li>
        </ul>
      </div>
    </template>

    <template #actions>
      <MedButton
        variant="primary"
        :disabled="pendingAjaxRequests > 0 || !type"
        :data-test="getTestId('print-pdf-dialog-create-button')"
        @click="print()"
      >
        Erstellen
      </MedButton>

      <DeveloperContainer v-if="type">
        <MedButton variant="dev" :data-test="getTestId('print-pdf-dialog-debug-button')" @click="debugHtml()">
          Debug HTML
        </MedButton>
      </DeveloperContainer>

      <MedButton :data-test="getTestId('print-pdf-dialog-cancel-button')" @click="close()">Abbrechen</MedButton>
    </template>
  </ComponentDialog>
</template>
